// src/components/features/Interior/pages/InteriorFormPage.jsx
// URL: /interior/:projectId?token=xxx&updatedby=admin
//
// DYNAMIC STEP FLOW:
//  0 → InteriorStep1PropertyType   (Property type + floors + nature)
//  1 → InteriorStep2DesignTheme    (Theme swipe + color palette)
//  2 → InteriorStep3UnitDescription (Floor chip room selector)
//  3+ → InteriorRoomWizard         (One step per selected room, Q by Q inside)
//
// After Step 3, steps are auto-generated from floorRooms selection.
// e.g. if user picks Kitchen + Bedroom on Floor 1, Living on Floor 2:
//   Step 4 = Ground Floor / Kitchen  (Q1…Q18)
//   Step 5 = Ground Floor / Bedroom  (Q1…Q12)
//   Step 6 = First Floor / Living    (Q1…Q15)

import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box, Flex, useColorModeValue, useBreakpointValue,
  useToast, Spinner, Center, Text, Badge,
} from "@chakra-ui/react";

import {
  isAnyAuthenticated, saveRedirectUrl, saveAdminToken,
  getAdminToken, getUpdatedBy, getCustomerToken,
} from "../../../../utils/storage";
import {
  fetchFormData, postFormData, putFormData, moveCardToInProgress,
} from "../../../../utils/api";

import InteriorStep1PropertyType from "../components/InteriorStep1PropertyType";
import InteriorStep2DesignTheme from "../components/InteriorStep2DesignTheme";
import InteriorStep3UnitDescription from "../components/InteriorStep3UnitDescription";
import InteriorRoomWizard from "../components/InteriorRoomWizard";
import { ROOM_TYPES, ROOM_QUESTIONS } from "../data/interiorRoomData";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const FLOOR_NAMES = ["Ground Floor", "First Floor", "Second Floor", "Third Floor", "Fourth Floor", "Fifth Floor"];

const DEFAULT_FORM_DATA = {
  propertyType: "",
  residentialSub: "",
  interiorFloors: "1",
  natureOfProject: "",
  interiorTheme: "",
  interiorLikedThemes: [],
  interiorColor: "",
  floorRooms: {},
  interiorRoomData: {},
};

// Build ordered list of room steps from floorRooms selection
function buildRoomSteps(floorRooms, numberOfFloors) {
  const steps = [];
  const floorLabels = Array.from({ length: numberOfFloors }, (_, i) => FLOOR_NAMES[i] || `Floor ${i + 1}`);
  floorLabels.forEach((floorLabel) => {
    const rooms = floorRooms[floorLabel] || [];
    rooms.forEach((roomId) => {
      const room = ROOM_TYPES.find((r) => r.id === roomId);
      steps.push({ floorLabel, roomId, roomLabel: room?.label || roomId });
    });
  });
  return steps;
}

const FIXED_STEPS = 3; // steps 0, 1, 2

export default function InteriorFormPage() {
  const { projectId: projectIdFromPath } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  // "/interior/:projectId" (path param) aur "/interior?projectId=xxx"
  // (query param) — dono URL shapes se projectId mil sake, isliye fallback.
  const projectId =
    projectIdFromPath ||
    searchParams.get("projectId") ||
    searchParams.get("pid") ||
    searchParams.get("id") ||
    "";

  const urlToken = searchParams.get("token") || "";
  const urlUpdatedBy = searchParams.get("updatedby") || "";
  const formVersion = searchParams.get("version") || "";
  // VIEW MODE: ?viewMode=true par form sirf padhne ke liye khulega —
  // next/previous chalega par koi PUT/POST API call nahi hogi, aur
  // thank-you page pe bhi kabhi redirect nahi hoga.
  const viewMode = searchParams.get("viewMode") === "true";

  if (urlToken) saveAdminToken(urlToken, urlUpdatedBy);

  const isAdmin = !!(urlToken || (urlUpdatedBy === "admin" && getAdminToken()));
  const token = urlToken || (isAdmin ? getAdminToken() : getCustomerToken()) || "";
  const updatedBy = isAdmin ? "admin" : (urlUpdatedBy || getUpdatedBy() || "user");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [version, setVersion] = useState(null);
  // Per-room: track which question index we're on for each room step
  const [roomQIdx, setRoomQIdx] = useState({}); // { "roomStepIndex": qIdx }
  const moveCardCalled = useRef(false);

  const containerWidth = useBreakpointValue({ base: "100%", md: "400px" });
  const pageBg = useColorModeValue("gray.100", "gray.900");
  const cardBg = useColorModeValue("gray.50", "gray.800");

  // Dynamic room steps built from selection
  const roomSteps = useMemo(() => {
    const floors = Math.min(parseInt(formData.interiorFloors || "1") || 1, 6);
    return buildRoomSteps(formData.floorRooms || {}, floors);
  }, [formData.floorRooms, formData.interiorFloors]);

  const TOTAL_STEPS = FIXED_STEPS + roomSteps.length;

  // ── Fetch existing data ────────────────────────────────────────────────────
  const initialize = useCallback(async () => {
    if (!token && !isAnyAuthenticated()) {
      saveRedirectUrl(window.location.pathname + window.location.search);
      toast({ title: "Authentication Required", status: "info", duration: 3000, isClosable: true });
      setTimeout(() => navigate("/login"), 1500);
      return;
    }
    if (!projectId) { setIsLoading(false); return; }

    try {
      setIsLoading(true);
      setApiError(null);
      const apiData = await fetchFormData(projectId, token, updatedBy, formVersion);
      // NEW: view mode me kabhi bhi thank-you pe redirect nahi karna — view
      // mode sirf data dekhne ke liye hai, status kuch bhi ho poora form
      // dikhna chahiye.
      if (apiData._meta?.status === "submitted" && !viewMode) { navigate("/thank-you"); return; }
      if (apiData._meta?.version) setVersion(apiData._meta.version);
      const { _meta, ...clean } = apiData;
      setFormData((prev) => ({ ...DEFAULT_FORM_DATA, ...prev, ...clean }));
      toast({
        title: "Project loaded ✓",
        description: `${projectId} — ${updatedBy === "admin" ? "Admin" : "User"} mode`,
        status: "success", duration: 3000, isClosable: true, position: "top",
      });
      // View mode me server ka state change nahi karna, isliye skip.
      if (!moveCardCalled.current && !viewMode) {
        moveCardCalled.current = true;
        await moveCardToInProgress(projectId, token, updatedBy, apiData._meta?.version || formVersion);
      }
    } catch (err) {
      setApiError(err.message);
      // View mode me error toast nahi dikhana — sirf state me set rahega
      if (!viewMode) {
        toast({
          title: "Could not load project",
          description: `${err.message} — using blank form`,
          status: "warning", duration: 5000, isClosable: true, position: "top",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [projectId, token, updatedBy, navigate, toast, viewMode, formVersion]);

  useEffect(() => { initialize(); }, [initialize]);

  const putToApi = async (data) => {
    // View mode: koi save API nahi chalegi — sirf navigation allowed
    if (!projectId || viewMode) return;
    setIsSaving(true);
    try { await putFormData(projectId, data, updatedBy, token, version); }
    catch (err) { toast({ title: "Step save failed", description: err.message, status: "error", duration: 3000, isClosable: true, position: "top-right" }); }
    finally { setIsSaving(false); }
  };

  const postToApi = async (data) => {
    // View mode: final submit bhi server par nahi jaayega
    if (!projectId || viewMode) return;
    setIsSaving(true);
    try { await postFormData(projectId, data, updatedBy, token, version); }
    catch (err) { toast({ title: "Save failed", description: err.message, status: "error", duration: 3000, isClosable: true, position: "top-right" }); }
    finally { setIsSaving(false); }
  };

  const goNext = async (latestData) => {
    await putToApi(latestData ?? formData);
    setStep((p) => Math.min(p + 1, TOTAL_STEPS - 1));
  };

  const goPrev = () => setStep((p) => Math.max(p - 1, 0));
  const isLastStep = step === TOTAL_STEPS - 1;

  const handleSubmit = async (latestData) => {
    // View mode: na POST, na thank-you redirect — form sirf dekhne ke liye hai
    if (viewMode) return;
    await postToApi(latestData ?? formData);
    navigate("/thank-you");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const renderStep = () => {
    const props = {
      formData, setFormData,
      onBack: goPrev, onNext: goNext, onSubmit: handleSubmit,
      isLastStep, projectId, updatedBy, token, viewMode,
    };

    if (step === 0) return <InteriorStep1PropertyType    {...props} />;
    if (step === 1) return <InteriorStep2DesignTheme     {...props} />;
    if (step === 2) return <InteriorStep3UnitDescription {...props} />;

    // Dynamic room steps
    const roomStepIndex = step - FIXED_STEPS;
    const roomStep = roomSteps[roomStepIndex];
    if (!roomStep) return null;

    const currentRoomStep = roomQIdx[step] || 0;
    const questions = ROOM_QUESTIONS[roomStep.roomId] || [];
    const isLastQ = currentRoomStep === questions.length - 1;

    return (
      <InteriorRoomWizard
        {...props}
        roomId={roomStep.roomId}
        floorLabel={roomStep.floorLabel}
        roomLabel={roomStep.roomLabel}
        currentRoomStep={currentRoomStep}
        onRoomStepChange={(newIdx) => setRoomQIdx((prev) => ({ ...prev, [step]: newIdx }))}
        // override onNext: only advance main step when last question is done
        onNext={async (latestData) => {
          if (isLastQ) {
            await putToApi(latestData ?? formData);
            setStep((p) => Math.min(p + 1, TOTAL_STEPS - 1));
            // reset q index for next room
          } else {
            setRoomQIdx((prev) => ({ ...prev, [step]: currentRoomStep + 1 }));
          }
        }}
        onBack={() => {
          if (currentRoomStep > 0) {
            setRoomQIdx((prev) => ({ ...prev, [step]: currentRoomStep - 1 }));
          } else {
            setStep((p) => Math.max(p - 1, 0));
          }
        }}
        isLastStep={isLastStep && isLastQ}
      />
    );
  };

  // ── Step label for header ─────────────────────────────────────────────────
  const getStepLabel = () => {
    if (step === 0) return "Property Type";
    if (step === 1) return "Design Theme";
    if (step === 2) return "Select Rooms";
    const rs = roomSteps[step - FIXED_STEPS];
    if (rs) {
      const qIdx = roomQIdx[step] || 0;
      const totalQ = (ROOM_QUESTIONS[rs.roomId] || []).length;
      return `${rs.roomLabel} · Q${qIdx + 1}/${totalQ}`;
    }
    return "";
  };

  // ── Progress ──────────────────────────────────────────────────────────────
  // Show overall progress across all main steps,
  // plus within-room sub-progress when inside a room wizard
  const isRoomStep = step >= FIXED_STEPS;
  const roomStepIdx = step - FIXED_STEPS;
  const curRoomQ = roomQIdx[step] || 0;
  const curRoomTotalQ = isRoomStep
    ? (ROOM_QUESTIONS[roomSteps[roomStepIdx]?.roomId] || []).length
    : 0;

  // Main bar: which main step we're on
  const mainPct = TOTAL_STEPS > 1 ? (step / (TOTAL_STEPS - 1)) * 100 : 100;
  // Sub bar (within room): question progress
  const subPct = curRoomTotalQ > 1 ? (curRoomQ / (curRoomTotalQ - 1)) * 100 : 0;

  if (isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={pageBg}>
        <Center flexDirection="column" gap={4}>
          <Spinner size="xl" color="cyan.500" thickness="4px" />
          <Text color="gray.500" fontSize="sm">Loading {projectId}...</Text>
        </Center>
      </Flex>
    );
  }

  return (
    <Flex minH="100vh" align="center" justify="center" bg={pageBg}>
      <Box
        w={containerWidth}
        bg={cardBg}
        px={6} py={6}
        borderRadius="lg"
        boxShadow="lg"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <Flex justify="space-between" align="center" mb={2}>
          <Flex align="center" gap={2} flexWrap="wrap">
            <Text fontSize="11px" color="gray.400" fontWeight="500">
              Step {step + 1} / {TOTAL_STEPS}
            </Text>
            <Text fontSize="11px" color="gray.500" noOfLines={1} maxW="160px">
              {getStepLabel()}
            </Text>
            {projectId && (
              <Badge fontSize="10px" colorScheme="cyan" variant="subtle">{projectId}</Badge>
            )}
            {updatedBy === "admin" && (
              <Badge fontSize="10px" colorScheme="purple" variant="subtle">Admin</Badge>
            )}
            <Badge fontSize="10px" colorScheme="pink" variant="subtle">Interior</Badge>
            {viewMode && (
              <Badge fontSize="10px" colorScheme="orange" variant="solid">
                View Mode
              </Badge>
            )}
          </Flex>
          <Flex align="center" gap={1}>
            {isSaving && <Text fontSize="10px" color="cyan.500" fontWeight="600">Saving...</Text>}
            {apiError && <Badge fontSize="10px" colorScheme="orange" variant="subtle">⚠ Offline</Badge>}
          </Flex>
        </Flex>

        {/* Progress bars — main + sub (when in room wizard) */}
        <Box mb={4}>
          {/* Main step progress */}
          <Box bg="gray.200" borderRadius="full" h="3px">
            <Box
              bg="cyan.500" borderRadius="full" h="3px"
              w={`${mainPct}%`} transition="width 0.3s ease"
            />
          </Box>
          {/* Sub-question progress (only when in a room step) */}
          {isRoomStep && curRoomTotalQ > 0 && (
            <Box bg="gray.100" borderRadius="full" h="2px" mt={1}>
              <Box
                bg="cyan.300" borderRadius="full" h="2px"
                w={`${subPct}%`} transition="width 0.3s ease"
              />
            </Box>
          )}
        </Box>

        <Box flex="1">{renderStep()}</Box>
      </Box>
    </Flex>
  );
}